import React, { useContext, useEffect, useState } from 'react';
import { Button, Col, Row, Table, Typography } from 'antd';
import { PageContainer, PageContent, SectionHeader } from '../../common';
import { Translate } from '../../../services/translationService/components/Translate';
import { BreadcrumbContext } from '../../../breadcrumbs/BreadcrumbContext';
import { useSanitizer } from '../../common/learningItem/sanitizers';
import { useTranslations } from '../../../hooks/translations/useTranslations';
import { useAuthState } from '../../../hooks/auth';
import './styles.less';
import { useQuery } from '@apollo/react-hooks';
import { MINIMAL_SEARCH_QUERY } from '../../../gql/queries/MINIMAL_SEARCH_QUERY';
import { ENROLLMENTS_QUERY } from '../../../gql/queries/ENROLLMENTS_QUERY';
import { ACTIVE_BRAND_UUID } from '../../../branding';

export const MyLearningTranscript = ( { pageKey } ) => {
    const { locale } = useTranslations(); 

    const { claims } = useAuthState();

    const { sanitizeTranscript } = useSanitizer();
  
    const { leaveCrumb } = useContext( BreadcrumbContext );   
  
    useEffect( () => { leaveCrumb( pageKey ); }, [] );

    const [ transcriptData, setTranscriptData ] = useState( [] );
    const [ loading, setLoading ] = useState( true );

    const { data: courseData, loading: searchLoading } = useQuery( MINIMAL_SEARCH_QUERY, {
        variables: {
            types: [ 'courses' ],
            query: '',
            page: 1,
            limit: 1000, 
            sort: 'name',
            order: 'asc',
            personId: claims.person_id,
        },
    } );

    const { data: enrollmentsData, loading: enrollmentsLoading } = useQuery( ENROLLMENTS_QUERY, { variables: { brandId: ACTIVE_BRAND_UUID } } );

    useEffect( () => {
        if ( searchLoading || enrollmentsLoading ) {return;}

        setLoading( false );

        const completedCourses = ( enrollmentsData?.enrollments?.edges ?? [] )
            .map( e => e?.node )
            .filter( e => e.status == 'COMPLETED' )
            .map( e => ( {
                    ...e,
                    course: courseData?.search?.edges?.find( c => c.node.courseId == e.courseId )?.node
            } ) )
            .filter( e => !!e.course )
            .map( e => ( {
                ...e,
                duration: e.course.learningObjects[ 0 ].duration,
                name: e.course.name,
            } ) );

        const sanitized = sanitizeTranscript( completedCourses );

        setTranscriptData( sanitized );

    }, [ enrollmentsData, courseData ] );
    
    const columns = [
        {
            title: <Translate.Text value="transcript.course.title" />,
            dataIndex: 'name',
            key: 'name',
            sortDirections: [ 'ascend', 'descend' ],
            sorter: ( a, b ) => a.name.localeCompare( b.name ),
            fixed: 'left'
        },
        {
            title: <Translate.Text value="transcript.completion.date" />,
            dataIndex: 'completion',
            key: 'completion',
            sortDirections: [ 'ascend', 'descend' ],
            sorter: ( a, b ) => new Date( a.completion ) - new Date( b.completion ),
        },
        {
            title: <Translate.Text value="transcript.duration" />,
            dataIndex: 'duration',
            key: 'duration',
            sortDirections: [ 'ascend', 'descend' ],
            render: ( duration ) => <>{ duration >= 0 ? `${ duration } Min` : 'N/A' }</>,
            sorter: ( a, b ) => a.duration - b.duration,
        },
        {
            title: <Translate.Text value="transcript.certificate" />,
            dataIndex: 'certificate',
            key: 'certificate',
            sortDirections: [ 'ascend', 'descend' ],
            sorter: ( a, b ) => a.certificate.localeCompare( b.certificate ),
        },
        {
            title: <Translate.Text value="transcript.score" />,
            dataIndex: 'score',
            key: 'score',
            width: 110,
            render: ( score ) => <>{ score >= 0 ? score : 'N/A' }</>,
            sortDirections: [ 'ascend', 'descend' ],
            sorter: ( a, b ) => a.score - b.score,
        },
        {
            title: <Translate.Text value="transcript.view.course.details" />,
            dataIndex: 'viewPath',
            key: 'viewPath',
            width: 275,
            className: 'no-print',
            render: ( id ) => <Button href={ id }><Translate.Text value="transcript.view.course.details" /></Button>
        },
    ];
  
    const getFormattedDate = () => {
        const date = new Date( Date.now() );
    
        if ( locale === 'en-US' || locale === 'en-CA' ) {
            return `As of ${ date.toLocaleString( 'default', { month: 'long' } ) } ${ date.getDate() }, ${ date.getFullYear() }`;
        } else {
            return `As of ${ date.getDate() } ${ date.toLocaleString( 'default', { month: 'long' } ) }, ${ date.getFullYear() }`;
        }
    }; 
  
    return (
        <PageContent className="page-transcript">
            <PageContainer>
                <SectionHeader>
                    <Translate.Text value="application.windowTitle.myLearningTranscript" />
                </SectionHeader>
                <div className='transcript-page-container'>
                    <Row className='transcript-page-header'>
                        <Col flex="auto">
                            <Typography.Title className='transcript-page-heading' level={ 3 }>
                                <Translate.Text value="transcript.completed.courses" />
                                <p className="as-of"><small>{ getFormattedDate() }</small></p>
                                <p className='print-only'>{ claims.name }</p>
                            </Typography.Title>
                        </Col>
                        <Col className='no-print' flex="auto" style={ { textAlign: 'right' } }>
                            <Button 
                                onClick={ () => window.print() } 
                                type='primary'
                                disabled={ loading }>
                                <Translate.Text value="transcript.print" />
                            </Button>
                        </Col>
                    </Row>
                    <Table 
                        dataSource={ transcriptData } 
                        columns={ columns } 
                        scroll={ { x: 1000 } }
                        pagination={ false }
                        loading={ loading }
                    />
                </div>
            </PageContainer>
        </PageContent>
    );
};
